import { BridgeInputMessage, BridgeOutputMessage } from './bridgeBase';

/**
 * Enum defining all possible message types that can be exchanged between
 * the Runtime Bridge clients and worker.
 */
export enum RuntimeBridgeWorkerMessageType {
    // Input message types (client to worker)
    uploadResponse = "uploadResponse",
    startScreenRecording = "startScreenRecording",
    stopScreenRecording = "stopScreenRecording",
    frontendReady = "frontendReady",
    changeKeyboardLanguage = "changeKeyboardLanguage",
    listKeyboardLanguagesAvailable = "listKeyboardLanguagesAvailable",
    setRuntimeLanguage = "setRuntimeLanguage",

    // Output message types (worker to client)
    uploadProgress = "uploadProgress",
    uploadFailed = "uploadFailed",
    uploadSuccess = "uploadSuccess",

    screenRecordingStopped = "screenRecordingStopped",

    quit = "quit"
}

// Input Messages (Client to Worker)
/**
 * Message sent to initiate file upload.
 */
export class UploadResponseMessage extends BridgeInputMessage {
    /** The name of the file to upload. */
    filename: string;
    /** The API URL to use for requesting the upload URL. */
    apiUrl: string;
    /** The UUID of the assessment for which the response is being uploaded. */
    assessmentUuid: string;
    /** Optional file data blob, if already available. */
    fileData?: Blob;
    /** Optional flag to delete temporary response data store after successful upload. */
    deleteTemporaryResponseDataOnSuccess?: boolean;
    /** Optional PendingRecoveryEntry ID — deleted on upload success alongside temporary response data. */
    recoveryEntryId?: number;

    /**
     * Creates a new UploadResponseMessage.
     * @param filename - The name of the file to upload
     * @param apiUrl - The API URL to use for requesting the upload URL
     * @param assessmentUuid - The UUID of the assessment for which the response is being uploaded
     * @param fileData - Optional file data blob to upload directly without retrieving from cache
     * @param deleteTemporaryResponseDataOnSuccess - Optional flag to delete temporary response data store after successful upload
     * @param recoveryEntryId - Optional PendingRecoveryEntry ID to delete on upload success
     */
    constructor(filename: string, apiUrl: string, assessmentUuid: string, fileData?: Blob, deleteTemporaryResponseDataOnSuccess?: boolean, recoveryEntryId?: number) {
        super();
        this.type = RuntimeBridgeWorkerMessageType.uploadResponse;
        this.filename = filename;
        this.apiUrl = apiUrl;
        this.assessmentUuid = assessmentUuid;
        this.fileData = fileData;
        this.deleteTemporaryResponseDataOnSuccess = deleteTemporaryResponseDataOnSuccess;
        this.recoveryEntryId = recoveryEntryId;
    }
}

/**
 * Message sent when the frontend is ready.
 */
export class FrontendReadyMessage extends BridgeInputMessage {
    constructor() {
        super();
        this.type = RuntimeBridgeWorkerMessageType.frontendReady;
    }
}

/**
 * Message sent to change the keyboard language.
 */
export class ChangeKeyboardLanguageMessage extends BridgeInputMessage {
    /** The new keyboard language code. */
    language: string;

    constructor(language: string) {
        super();
        this.type = RuntimeBridgeWorkerMessageType.changeKeyboardLanguage;
        this.language = language;
    }
}

/**
 * Message sent to request the list of available keyboard languages.
 */
export class ListKeyboardLanguagesAvailableMessage extends BridgeInputMessage {
    constructor() {
        super();
        this.type = RuntimeBridgeWorkerMessageType.listKeyboardLanguagesAvailable;
    }
}

/**
 * Message sent to set the runtime language.
 */
export class SetRuntimeLanguageMessage extends BridgeInputMessage {
    /** The new runtime language code. */
    lang: string;

    constructor(lang: string) {
        super();
        this.type = RuntimeBridgeWorkerMessageType.setRuntimeLanguage;
        this.lang = lang;
    }
}

// Output Messages (Worker to Client)
/**
 * Message sent to report upload progress.
 */
export class UploadProgressMessage extends BridgeOutputMessage {
    /** Upload progress as a percentage (0-100). */
    progress: number;
    /** Human-readable progress message. */
    message: string;

    constructor(progress: number, message: string) {
        super();
        this.type = RuntimeBridgeWorkerMessageType.uploadProgress;
        this.progress = progress;
        this.message = message;
    }
}

/**
 * Message sent when upload fails.
 */
export class UploadFailedMessage extends BridgeOutputMessage {
    /** Error message detailing why the upload failed. */
    message: string;

    constructor(message: string) {
        super();
        this.type = RuntimeBridgeWorkerMessageType.uploadFailed;
        this.message = message;
    }
}

/**
 * Message sent when upload is successful.
 */
export class UploadSuccessMessage extends BridgeOutputMessage {
    /** Success message indicating completion of upload. */
    message: string;

    constructor(message: string) {
        super();
        this.type = RuntimeBridgeWorkerMessageType.uploadSuccess;
        this.message = message;
    }
}

/**
 * Message sent when screen recording is stopped.
 */
export class ScreenRecordingStoppedMessage extends BridgeOutputMessage {
    constructor() {
        super();
        this.type = RuntimeBridgeWorkerMessageType.screenRecordingStopped;
    }
}

export class QuitMessage extends BridgeInputMessage {
    constructor() {
        super();
        this.type = RuntimeBridgeWorkerMessageType.quit;
    }
}